/**
 * Socket Handlers — coordinator that delegates to domain-specific handler modules.
 *
 * Each module registers its own socket listeners and returns a cleanup function.
 * This file wires the delta buffer and composes all cleanups.
 */

import type { Socket } from "socket.io-client";
import { registerTaskLifecycleHandlers } from "./handlers/taskLifecycleHandlers";
import { registerStreamingHandlers } from "./handlers/streamingHandlers";
import { registerLastErrorStamp } from "./deskSignals";
import { registerInteractionHandlers } from "./handlers/interactionHandlers";
import { registerSyncHandlers } from "./handlers/syncHandlers";
import { registerTaskStreamHandlers } from "./handlers/taskStreamHandlers";
import { registerCreditHandlers } from "./handlers/creditHandlers";
import { registerLiveHandlers } from "./handlers/liveHandlers";
import { registerBoardHandlers } from "./handlers/boardHandlers";
import { registerFilesHandlers } from "./handlers/filesHandlers";
import { registerSupportHandlers } from "./handlers/supportHandlers";
import { registerContextPanelHandlers } from "./handlers/contextPanelHandlers";
import { registerRoutineHandlers } from "./handlers/routineHandlers";
import { registerItemHandlers } from "./handlers/itemHandlers";
import { registerSubagentHandlers } from "./handlers/subagentHandlers";
import { registerTeammateHandlers } from "./handlers/teammateHandlers";
import { registerAgentCrudHandlers } from "./handlers/agentCrudHandlers";
import { registerAdminMessageHandlers } from "./handlers/adminMessageHandlers";
import { registerInjectionScreenHandlers } from "./handlers/injectionScreenHandlers";
import { attachCapabilitiesHandler } from "./handlers/capabilitiesHandler";
import { attachBrowserStreamHandler } from "./handlers/browserStreamHandler";
import { attachTerminalStreamHandler } from "./handlers/terminalStreamHandler";
import { attachOsHandsHandler } from "./handlers/osHandsHandler";
import { attachLivePreviewHandler } from "./handlers/livePreviewHandler";
import { attachComputerVerbHandler } from "./handlers/computerVerbHandlers";
import { attachAppsHandler } from "./handlers/appsHandler";

import { registerSuggestionHandlers } from "./handlers/suggestionHandler";
import { registerJobHandlers } from "./handlers/jobHandlers";
import { registerStagingHandlers } from "./handlers/stagingHandlers";
import { registerReachHandlers } from "./handlers/reachHandlers";
import { registerNoticeHandlers } from "./handlers/noticeHandlers";

/**
 * Register all socket event handlers. Call once after socket creation.
 * Returns a cleanup function that removes all listeners.
 */
export function registerAllHandlers(socket: Socket): () => void {
  const cleanups = [
    registerTaskLifecycleHandlers(socket),
    // Streaming handlers — AGENT_START/END, TEXT_DELTA, THINKING_DELTA,
    // TOOL_USE, TOOL_RESULT, etc. Restored from ead4d42c^ after PR #1315
    // inadvertently deleted this file while cleaning up Path B.
    registerStreamingHandlers(socket),
    // Beside them, not inside: stamps WHEN a miss landed, which is the one
    // thing the desk's "trying again" needs and no renderer records.
    registerLastErrorStamp(socket),
    registerInteractionHandlers(socket),
    registerSyncHandlers(socket),
    registerTaskStreamHandlers(socket),
    registerCreditHandlers(socket),
    // The Line: composer:live:state and the relayed RouterEvents of a spoken turn.
    registerLiveHandlers(socket),
    // home-streams: board:task_delta → historyStore (push hint under the
    // tasks:list reconcile reads).
    registerBoardHandlers(socket),
    registerFilesHandlers(socket),
    registerSupportHandlers(socket),
    registerContextPanelHandlers(socket),
    registerRoutineHandlers(socket),
    registerItemHandlers(socket),
    registerSubagentHandlers(socket),
    // P5.4 Agent Teams — SUBAGENT_SPAWNED/SUBAGENT_MESSAGE (teammate lanes).
    registerTeammateHandlers(socket),
    registerAgentCrudHandlers(socket),
    registerAdminMessageHandlers(socket),
    // W4-2 shadow injection screen — the per-task "content check" notice.
    registerInjectionScreenHandlers(socket),
    registerSuggestionHandlers(socket),
    attachCapabilitiesHandler(socket),
    attachBrowserStreamHandler(socket),
    attachTerminalStreamHandler(socket),
    // OS-hands lane — OS_HANDS_ACTIVE ambient signal (agent operating the Mac).
    attachOsHandsHandler(socket),
    // Live Localhost Preview — TASK_PREVIEW_READY/CLOSED (ephemeral live iframe).
    attachLivePreviewHandler(socket),
    attachComputerVerbHandler(socket),
    // Local hosting — apps:changed (the user's app list, level-triggered).
    attachAppsHandler(socket),
    registerJobHandlers(socket),
    registerStagingHandlers(socket),
    // A3-T2 Reach — nami:notify, everything Nami says out of band. The parse in
    // the handler is the only gate this frame passes through.
    registerReachHandlers(socket),
    // Engines T0 — one quiet line in a task: a refused step (PERMISSION_DENIED
    // carries the relay's `notice`) or the runner's own line (TASK_NOTICE).
    registerNoticeHandlers(socket),
  ];

  return () => {
    cleanups.forEach((fn) => fn());
  };
}
